import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  BarChart3, Users, Search, Check, X, Ban, RotateCcw, LogOut, TrendingUp, LockKeyhole, KeyRound,
  RefreshCw, Filter, Sparkles, Flag, AlertTriangle, ExternalLink, ChevronDown, Gift, Heart,
} from "lucide-react";
import api, { setAdminToken, getAdminToken } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

const TABS = [
  { id: "stats", icon: BarChart3, label: "Статистика" },
  { id: "users", icon: Users, label: "Пользователи" },
  { id: "reports", icon: Flag, label: "Жалобы", default: true },
  { id: "utm", icon: ExternalLink, label: "UTM" },
  { id: "referrals", icon: Gift, label: "Рефералы" },
  { id: "premium", icon: Sparkles, label: "Premium" },
];

const PREMIUM_TIER_LABELS = { 2: "2 дня", 30: "30 дней", 90: "90 дней", 180: "180 дней" };

function StatCard({ label, value, hint, testid }) {
  return (
    <div
      className="rounded-3xl bg-white border border-[color:var(--border)] p-4 shadow-sm shadow-[#E76F51]/10"
      data-testid={testid}
    >
      <div className="text-[11px] uppercase tracking-widest text-[color:var(--ink-soft)]">{label}</div>
      <div className="font-[Onest] font-extrabold text-3xl mt-1">{value ?? "—"}</div>
      {hint && <div className="text-xs text-[color:var(--ink-soft)] mt-1">{hint}</div>}
    </div>
  );
}

function StatsPanel() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    if (silent) setUpdating(true);
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
      setUpdatedAt(new Date());
    } catch (e) {
      toast.error("Не удалось загрузить статистику");
    } finally {
      setLoading(false);
      setUpdating(false);
    }
  };
  useEffect(() => {
    load(false);
    const id = setInterval(() => load(true), 20000);
    return () => clearInterval(id);
  }, []);

  if (loading) return <div className="text-center py-10 text-[color:var(--ink-soft)]">Загружаем…</div>;
  if (!stats) return null;

  const total = stats.total_users || 0;
  const malePct = total ? Math.round((stats.males / total) * 100) : 0;
  const femPct = total ? Math.round((stats.females / total) * 100) : 0;

  return (
    <div className="space-y-4">
      <div className="rounded-3xl bg-white/75 backdrop-blur-sm border border-[color:var(--border)] p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--ink-soft)]">Обзор платформы</div>
          <div className="text-sm text-[color:var(--ink-soft)] mt-1">
            Последнее обновление: {updatedAt ? updatedAt.toLocaleTimeString("ru-RU") : "-"}
          </div>
        </div>
        <button
          data-testid="admin-stats-refresh"
          onClick={() => load(true)}
          disabled={updating}
          className="btn-pill bg-[color:var(--ink)] text-white text-sm px-5 disabled:opacity-60"
        >
          <span className="inline-flex items-center gap-2">
            <RefreshCw className={`w-4 h-4 ${updating ? "animate-spin" : ""}`} />
            {updating ? "Обновляем..." : "Обновить"}
          </span>
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <StatCard label="Всего" value={stats.total_users} testid="stat-total" />
        <StatCard label="За сегодня" value={`+${stats.today}`} hint="новых регистраций" testid="stat-today" />
        <StatCard label="За неделю" value={`+${stats.this_week}`} testid="stat-week" />
        <StatCard label="Premium" value={stats.premium_users} testid="stat-premium" />
        <StatCard label="Мэтчей" value={stats.matches} testid="stat-matches" />
        <StatCard label="Лайков сегодня" value={stats.likes_today} testid="stat-likes-today" />
      </div>

      <div className="rounded-3xl bg-white p-5 border border-[color:var(--border)]">
        <div className="text-sm font-semibold text-[color:var(--ink-soft)] mb-3 flex items-center gap-2">
          <TrendingUp className="w-4 h-4" />
          Распределение по полу
        </div>
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <div className="flex justify-between text-xs mb-1">
              <span>♂ {stats.males} ({malePct}%)</span>
              <span>♀ {stats.females} ({femPct}%)</span>
            </div>
            <div className="h-3 rounded-full overflow-hidden bg-[color:var(--peach)]/15 flex">
              <div
                className="h-full bg-gradient-to-r from-[#2A9D8F] to-[#4CB5A0]"
                style={{ width: `${malePct}%` }}
              />
              <div
                className="h-full bg-gradient-to-r from-[#F4A261] to-[#E76F51]"
                style={{ width: `${femPct}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-3xl bg-white p-5 border border-[color:var(--border)]">
        <div className="text-sm font-semibold text-[color:var(--ink-soft)] mb-3">По странам</div>
        <div className="space-y-2">
          {Object.entries(stats.by_country || {}).map(([code, n]) => (
            <div key={code} className="flex items-center justify-between text-sm">
              <span className="uppercase font-semibold">{code}</span>
              <span className="text-[color:var(--ink-soft)]">{n}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Историй активных" value={stats.stories_active} testid="stat-stories" />
        <StatCard label="Всего лайков" value={stats.likes_total} testid="stat-likes-total" />
      </div>

      {stats.landing_campaigns && Object.keys(stats.landing_campaigns).length > 0 && (
        <div className="rounded-3xl bg-white border border-[color:var(--border)] p-5">
          <div className="text-sm font-semibold text-[color:var(--ink-soft)] mb-3">Лендинги</div>
          <div className="space-y-2">
            {Object.entries(stats.landing_campaigns).map(([campaign, d]) => (
              <div key={campaign} className="flex items-center justify-between text-sm">
                <span className="font-semibold">{campaign}</span>
                <span className="text-[color:var(--ink-soft)]">{d.rewarded} награждено / {d.registered} регистраций</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function UsersPanel() {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserMatches, setSelectedUserMatches] = useState([]);
  const [busyUserId, setBusyUserId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [premiumOnly, setPremiumOnly] = useState(false);
  const [premiumDays, setPremiumDays] = useState(30);
  const [grantingPremium, setGrantingPremium] = useState(false);

  const search = async (query = q) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users`, { params: { q: query, limit: 60 } });
      setItems(data.items || []);
    } catch {
      toast.error("Ошибка поиска");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { search(""); /* eslint-disable-next-line */ }, []);

  const visibleItems = items.filter((u) => {
    if (statusFilter === "active" && u.is_active === false) return false;
    if (statusFilter === "banned" && u.is_active !== false) return false;
    if (premiumOnly && !u.is_premium) return false;
    return true;
  });

  const activeCount = items.filter((u) => u.is_active !== false).length;
  const bannedCount = items.filter((u) => u.is_active === false).length;
  const premiumCount = items.filter((u) => u.is_premium).length;

  const toggleBan = async (u) => {
    setBusyUserId(u.id);
    try {
      const path = u.is_active ? "ban" : "unban";
      await api.post(`/admin/users/${u.id}/${path}`);
      toast.success(u.is_active ? "Забанен" : "Разбанен");
      search();
    } catch {
      toast.error("Ошибка");
    } finally {
      setBusyUserId(null);
    }
  };

  const openUser = async (u) => {
    setBusyUserId(u.id);
    try {
      const { data } = await api.get(`/admin/users/${u.id}`);
      setSelectedUser(data.user || null);
      setSelectedUserMatches(data.matches || []);
    } catch {
      toast.error("Не удалось открыть профиль");
    } finally {
      setBusyUserId(null);
    }
  };

  const deleteMatch = async (matchId) => {
    const ok = window.confirm("Удалить матч? Это также сотрёт переписку и лайки между этими двумя — можно будет залайкать друг друга заново.");
    if (!ok) return;
    setBusyUserId(selectedUser?.id);
    try {
      await api.delete(`/admin/matches/${matchId}`);
      toast.success("Матч удалён");
      const { data } = await api.get(`/admin/users/${selectedUser.id}`);
      setSelectedUser(data.user || null);
      setSelectedUserMatches(data.matches || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Не удалось удалить матч");
    } finally {
      setBusyUserId(null);
    }
  };

  const grantPremium = async () => {
    if (!selectedUser) return;
    const days = Number(premiumDays);
    if (!Number.isInteger(days) || days <= 0) {
      toast.error("Введите целое число дней больше нуля");
      return;
    }
    setGrantingPremium(true);
    try {
      const { data } = await api.post(`/admin/users/${selectedUser.id}/premium`, { days });
      setSelectedUser((prev) => prev && ({ ...prev, is_premium: data.is_premium, premium_until: data.premium_until }));
      toast.success(`Выдано ${days} дн. Premium`);
      search();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Не удалось выдать Premium");
    } finally {
      setGrantingPremium(false);
    }
  };

  const deleteUser = async (u) => {
    const ok = window.confirm(`Удалить профиль ${u.first_name || u.id}? Действие необратимо.`);
    if (!ok) return;
    setBusyUserId(u.id);
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Профиль удален");
      if (selectedUser?.id === u.id) setSelectedUser(null);
      search();
    } catch {
      toast.error("Не удалось удалить профиль");
    } finally {
      setBusyUserId(null);
    }
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-2xl bg-white border border-[color:var(--border)] p-3">
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--ink-soft)]">Активные</div>
          <div className="font-[Onest] text-xl font-extrabold">{activeCount}</div>
        </div>
        <div className="rounded-2xl bg-white border border-[color:var(--border)] p-3">
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--ink-soft)]">В бане</div>
          <div className="font-[Onest] text-xl font-extrabold">{bannedCount}</div>
        </div>
        <div className="rounded-2xl bg-white border border-[color:var(--border)] p-3">
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--ink-soft)]">Premium</div>
          <div className="font-[Onest] text-xl font-extrabold">{premiumCount}</div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-2 md:items-center">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[color:var(--ink-soft)]" />
          <input
            data-testid="admin-user-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
            placeholder="Имя, город, @username, telegram_id…"
            className="w-full pl-11 pr-4 py-3 rounded-2xl bg-white border border-[color:var(--border)] focus:outline-none focus:ring-2 focus:ring-[color:var(--peach)]"
          />
        </div>
        <button
          data-testid="admin-user-search-btn"
          onClick={() => search()}
          className="btn-pill bg-[color:var(--ink)] text-white px-5 md:w-auto w-full"
        >
          Найти
        </button>
        <button
          data-testid="admin-user-clear-search"
          onClick={() => { setQ(""); search(""); }}
          className="btn-pill bg-white border border-[color:var(--border)] text-[color:var(--ink)] px-5 md:w-auto w-full"
        >
          Сброс
        </button>
      </div>

      <div className="rounded-2xl bg-white/70 backdrop-blur-sm border border-[color:var(--border)] p-3">
        <div className="flex items-center gap-2 text-xs text-[color:var(--ink-soft)] mb-2">
          <Filter className="w-4 h-4" />
          Фильтры отображения
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            data-testid="admin-filter-all"
            onClick={() => setStatusFilter("all")}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold ${statusFilter === "all" ? "bg-[color:var(--ink)] text-white" : "bg-white border border-[color:var(--border)] text-[color:var(--ink)]"}`}
          >
            Все ({items.length})
          </button>
          <button
            data-testid="admin-filter-active"
            onClick={() => setStatusFilter("active")}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold ${statusFilter === "active" ? "bg-[color:var(--teal-superlike)] text-white" : "bg-white border border-[color:var(--border)] text-[color:var(--ink)]"}`}
          >
            Активные ({activeCount})
          </button>
          <button
            data-testid="admin-filter-banned"
            onClick={() => setStatusFilter("banned")}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold ${statusFilter === "banned" ? "bg-red-500 text-white" : "bg-white border border-[color:var(--border)] text-[color:var(--ink)]"}`}
          >
            В бане ({bannedCount})
          </button>
          <button
            data-testid="admin-filter-premium"
            onClick={() => setPremiumOnly((v) => !v)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1 ${premiumOnly ? "bg-[color:var(--stars-gold)] text-[color:var(--ink)]" : "bg-white border border-[color:var(--border)] text-[color:var(--ink)]"}`}
          >
            <Sparkles className="w-3 h-3" /> Premium
          </button>
        </div>
      </div>

      <div className="text-xs text-[color:var(--ink-soft)] px-1">
        Показано пользователей: {visibleItems.length} из {items.length}
      </div>

      {loading && <div className="text-center py-8 text-[color:var(--ink-soft)]">Загружаем…</div>}
      {!loading && visibleItems.length === 0 && (
        <div className="text-center py-8 text-[color:var(--ink-soft)]">Никого не найдено.</div>
      )}

      <div className="space-y-2">
        {visibleItems.map((u) => (
          <div
            key={u.id}
            data-testid={`admin-user-${u.id}`}
            className={`rounded-3xl p-4 border ${u.is_active === false ? "bg-red-50 border-red-200" : "bg-white border-[color:var(--border)]"}`}
          >
            <div className="flex flex-col xl:flex-row xl:items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-[Onest] font-bold">{u.first_name || "—"}</span>
                  <span className="text-[color:var(--ink-soft)] text-sm">{u.age}</span>
                  {u.is_premium && (
                    <span className="px-1.5 py-0.5 rounded-full bg-[color:var(--stars-gold)] text-[10px] font-bold text-[color:var(--ink)]">
                      P
                    </span>
                  )}
                </div>
                <div className="text-xs text-[color:var(--ink-soft)] mt-0.5 truncate">
                  {u.gender} · {u.city || "—"} · {u.country?.toUpperCase()} · {u.photos_count} фото
                </div>
                <div className="text-[11px] text-[color:var(--ink-soft)] mt-0.5">
                  TG: {u.telegram_id || "—"} {u.username ? "@" + u.username : ""}
                </div>
                {u.utm_source && (
                  <div className="text-[10px] text-[color:var(--peach-deep)] mt-0.5">
                    UTM: {u.utm_source}{u.utm_medium ? " / " + u.utm_medium : ""}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2 xl:w-auto w-full">
                <button
                  data-testid={`admin-ban-${u.id}`}
                  onClick={() => toggleBan(u)}
                  disabled={busyUserId === u.id}
                  className={`btn-pill text-xs px-3 py-2 ${u.is_active === false ? "bg-[color:var(--teal-superlike)] text-white" : "bg-red-500 text-white"}`}
                >
                  {busyUserId === u.id ? (
                    <span className="flex items-center justify-center gap-1">...</span>
                  ) : u.is_active === false ? (
                    <span className="flex items-center justify-center gap-1"><RotateCcw className="w-3 h-3" />Разбан</span>
                  ) : (
                    <span className="flex items-center justify-center gap-1"><Ban className="w-3 h-3" />Бан</span>
                  )}
                </button>
                <button
                  data-testid={`admin-view-${u.id}`}
                  onClick={() => openUser(u)}
                  disabled={busyUserId === u.id}
                  className="btn-pill text-xs px-3 py-2 bg-[color:var(--ink)] text-white"
                >
                  {busyUserId === u.id ? "..." : "Просмотр"}
                </button>
                <button
                  data-testid={`admin-delete-${u.id}`}
                  onClick={() => deleteUser(u)}
                  disabled={busyUserId === u.id}
                  className="btn-pill text-xs px-3 py-2 bg-black text-white"
                >
                  {busyUserId === u.id ? "..." : "Удалить"}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {selectedUser && (
        <div className="fixed inset-0 z-40 bg-black/45 backdrop-blur-sm p-4 flex items-end sm:items-center justify-center">
          <div className="w-full max-w-3xl rounded-3xl bg-white border border-[color:var(--border)] p-5 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <div className="font-[Onest] font-extrabold text-xl">Профиль пользователя</div>
              <button
                onClick={() => setSelectedUser(null)}
                className="px-3 py-1 rounded-full bg-white border border-[color:var(--border)]"
              >
                Закрыть
              </button>
            </div>
            <div className="text-sm space-y-4">
              {(selectedUser.photos || []).length > 0 ? (
                <div>
                  <div className="text-xs uppercase tracking-widest text-[color:var(--ink-soft)] mb-2">Фото профиля</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {(selectedUser.photos || []).map((p) => (
                      <a key={p.id} href={p.data_url} target="_blank" rel="noreferrer" className="block">
                        <img
                          src={p.data_url}
                          alt="profile"
                          className="w-full aspect-[3/4] object-cover rounded-2xl border border-[color:var(--border)]"
                        />
                      </a>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-[color:var(--border)] p-4 text-[color:var(--ink-soft)] text-center">
                  У пользователя нет загруженных фото
                </div>
              )}

              <div className="rounded-2xl bg-[color:var(--peach-bg)] border border-[color:var(--border)] p-4">
                <div className="text-xs uppercase tracking-widest text-[color:var(--ink-soft)] mb-2 flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" /> Premium
                </div>
                <div className="text-sm mb-3">
                  {selectedUser.is_premium ? (
                    <span>Активен до <b>{selectedUser.premium_until ? new Date(selectedUser.premium_until).toLocaleString("ru-RU") : "—"}</b></span>
                  ) : (
                    <span className="text-[color:var(--ink-soft)]">Не активен</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="1"
                    value={premiumDays}
                    onChange={(e) => setPremiumDays(e.target.value)}
                    className="w-24 px-3 py-2 rounded-xl bg-white border border-[color:var(--border)] text-sm"
                  />
                  <span className="text-xs text-[color:var(--ink-soft)]">дней</span>
                  <button
                    onClick={grantPremium}
                    disabled={grantingPremium}
                    className="btn-pill bg-[color:var(--ink)] text-white text-xs px-4 py-2 ml-auto disabled:opacity-50"
                  >
                    {grantingPremium ? "..." : "Выдать"}
                  </button>
                </div>
              </div>

              <div className="rounded-2xl bg-white border border-[color:var(--border)] p-4">
                <div className="text-xs uppercase tracking-widest text-[color:var(--ink-soft)] mb-2 flex items-center gap-1.5">
                  <Heart className="w-3.5 h-3.5" /> Матчи ({selectedUserMatches.length})
                </div>
                {selectedUserMatches.length === 0 ? (
                  <div className="text-xs text-[color:var(--ink-soft)]">Матчей нет</div>
                ) : (
                  <div className="space-y-1.5">
                    {selectedUserMatches.map((m) => (
                      <div key={m.match_id} className="flex items-center justify-between gap-2 text-xs bg-[color:var(--peach-bg)]/40 rounded-xl px-3 py-2">
                        <div className="min-w-0 truncate">
                          <b>{m.other_user?.first_name || "—"}</b>
                          <span className="text-[color:var(--ink-soft)]"> · TG {m.other_user?.telegram_id || "—"} · {new Date(m.created_at).toLocaleDateString("ru-RU")}</span>
                        </div>
                        <button
                          onClick={() => deleteMatch(m.match_id)}
                          disabled={busyUserId === selectedUser.id}
                          className="flex-shrink-0 btn-pill bg-red-500 text-white text-[11px] px-3 py-1.5 disabled:opacity-50"
                        >
                          Удалить
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div><b>ID:</b> {selectedUser.id}</div>
                <div><b>Имя:</b> {selectedUser.first_name || "—"}</div>
                <div><b>Возраст:</b> {selectedUser.age ?? "—"}</div>
                <div><b>Город:</b> {selectedUser.city || "—"}</div>
                <div><b>Страна:</b> {selectedUser.country || "—"}</div>
                <div><b>TG:</b> {selectedUser.telegram_id || "—"} {selectedUser.username ? `@${selectedUser.username}` : ""}</div>
                <div><b>Статус:</b> {selectedUser.is_active === false ? "Заблокирован" : "Активен"}</div>
                <div><b>Жалобы:</b> {selectedUser.report_count ?? 0}</div>
                <div><b>Фото:</b> {(selectedUser.photos || []).length}</div>
                <div className="md:col-span-2"><b>Био:</b> {selectedUser.bio || "—"}</div>
                <div><b>Мировоззрение:</b> {selectedUser.worldview || "—"}</div>
                <div><b>Образование:</b> {selectedUser.education || "—"}</div>
                <div><b>Дети:</b> {selectedUser.children || "—"}</div>
                <div><b>Языки:</b> {(selectedUser.spoken_languages || []).join(", ") || "—"}</div>
                <div><b>Алкоголь:</b> {selectedUser.alcohol || "—"}</div>
                <div><b>Курение:</b> {selectedUser.smoking || "—"}</div>
                <div className="md:col-span-2"><b>Сфера работы:</b> {selectedUser.work_field || "—"}</div>
              </div>
              {selectedUser.utm_source && (
                <div className="mt-3 pt-3 border-t border-[color:var(--border)]">
                  <div className="text-[10px] uppercase tracking-widest text-[color:var(--ink-soft)] mb-1">UTM метки</div>
                  <div className="grid grid-cols-2 gap-1 text-xs">
                    <div><b>source:</b> {selectedUser.utm_source}</div>
                    {selectedUser.utm_medium && <div><b>medium:</b> {selectedUser.utm_medium}</div>}
                    {selectedUser.utm_campaign && <div><b>campaign:</b> {selectedUser.utm_campaign}</div>}
                    {selectedUser.utm_term && <div><b>term:</b> {selectedUser.utm_term}</div>}
                    {selectedUser.utm_content && <div><b>content:</b> {selectedUser.utm_content}</div>}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ReportsPanel() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("open");
  const [busyId, setBusyId] = useState(null);
  const [expandedReport, setExpandedReport] = useState(null);

  const load = async (status = statusFilter) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/reports", { params: { status } });
      setReports(data.reports || []);
    } catch {
      toast.error("Ошибка загрузки жалоб");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const handleAction = async (reportId, action) => {
    setBusyId(reportId);
    try {
      await api.post(`/admin/reports/${reportId}/${action}`);
      toast.success(action === "resolve" ? "Жалоба принята" : "Жалоба отклонена");
      setReports((prev) => prev.filter((r) => r.id !== reportId));
      setExpandedReport(null);
    } catch {
      toast.error("Ошибка");
    } finally {
      setBusyId(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "open": return "bg-red-500";
      case "resolved": return "bg-[color:var(--teal-superlike)]";
      case "dismissed": return "bg-[color:var(--ink-soft)]";
      default: return "bg-[color:var(--ink-soft)]";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "open": return "Открыта";
      case "resolved": return "Принята";
      case "dismissed": return "Отклонена";
      default: return status;
    }
  };

  if (loading) return <div className="text-center py-10 text-[color:var(--ink-soft)]">Загружаем…</div>;

  return (
    <div className="space-y-4">
      {/* Status filter pills */}
      <div className="rounded-2xl bg-white/70 backdrop-blur-sm border border-[color:var(--border)] p-3 flex items-center gap-2 overflow-x-auto">
        <button
          onClick={() => { setStatusFilter("open"); load("open"); }}
          className={`px-4 py-2 rounded-full text-xs font-semibold flex-shrink-0 transition-all ${statusFilter === "open" ? "bg-red-500 text-white shadow-md" : "bg-white border border-[color:var(--border)] text-[color:var(--ink)] hover:bg-red-50"}`}
        >
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-red-400" />
            Открытые
          </span>
        </button>
        <button
          onClick={() => { setStatusFilter("all"); load("all"); }}
          className={`px-4 py-2 rounded-full text-xs font-semibold flex-shrink-0 transition-all ${statusFilter === "all" ? "bg-[color:var(--ink)] text-white shadow-md" : "bg-white border border-[color:var(--border)] text-[color:var(--ink)] hover:bg-gray-50"}`}
        >
          Все
        </button>
        <button
          onClick={() => { setStatusFilter("resolved"); load("resolved"); }}
          className={`px-4 py-2 rounded-full text-xs font-semibold flex-shrink-0 transition-all ${statusFilter === "resolved" ? "bg-[color:var(--teal-superlike)] text-white shadow-md" : "bg-white border border-[color:var(--border)] text-[color:var(--ink)] hover:bg-teal-50"}`}
        >
          <span className="inline-flex items-center gap-1.5">
            <Check className="w-3 h-3" />
            Решённые
          </span>
        </button>
      </div>

      {/* Summary cards */}
      {reports.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          <div className="rounded-2xl bg-white border border-[color:var(--border)] p-3 text-center">
            <div className="text-[10px] uppercase tracking-widest text-[color:var(--ink-soft)]">Всего</div>
            <div className="font-[Onest] font-extrabold text-xl">{reports.length}</div>
          </div>
          <div className="rounded-2xl bg-white border border-[color:var(--border)] p-3 text-center">
            <div className="text-[10px] uppercase tracking-widest text-[color:var(--ink-soft)]">На рассмотрении</div>
            <div className="font-[Onest] font-extrabold text-xl text-red-500">{reports.filter(r => r.status === "open").length}</div>
          </div>
          <div className="rounded-2xl bg-white border border-[color:var(--border)] p-3 text-center">
            <div className="text-[10px] uppercase tracking-widest text-[color:var(--ink-soft)]">Решено</div>
            <div className="font-[Onest] font-extrabold text-xl text-[color:var(--teal-superlike)]">{reports.filter(r => r.status !== "open").length}</div>
          </div>
        </div>
      )}

      {reports.length === 0 && (
        <div className="text-center py-16 text-[color:var(--ink-soft)]">
          <div className="text-4xl mb-3">✅</div>
          <div className="font-[Onest] font-bold text-lg">Чисто</div>
          <div className="text-sm mt-1">{statusFilter === "open" ? "Нет открытых жалоб" : "Жалоб не найдено"}</div>
        </div>
      )}

      {/* Reports list */}
      <div className="space-y-3">
        {reports.map((r) => {
          const reportedUser = r.reported_user || {};
          const reporter = r.reporter || {};
          const isOpen = expandedReport === r.id;
          return (
            <div
              key={r.id}
              className="rounded-3xl bg-white border border-[color:var(--border)] overflow-hidden transition-all hover:shadow-md"
            >
              {/* Clickable header */}
              <button
                onClick={() => setExpandedReport(isOpen ? null : r.id)}
                className="w-full text-left p-4 flex items-start gap-3 hover:bg-[color:var(--peach-bg)]/50 transition-colors"
              >
                <div className={`w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 ${r.status === "open" ? "bg-red-50" : r.status === "resolved" ? "bg-teal-50" : "bg-gray-50"}`}>
                  <AlertTriangle className={`w-5 h-5 ${r.status === "open" ? "text-red-500" : r.status === "resolved" ? "text-[color:var(--teal-superlike)]" : "text-[color:var(--ink-soft)]"}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-[Onest] font-bold text-sm truncate flex items-center gap-2">
                      {reportedUser.first_name || "—"}
                      <span className={`px-2 py-0.5 rounded-full text-[9px] font-semibold text-white ${getStatusColor(r.status)}`}>
                        {getStatusLabel(r.status)}
                      </span>
                    </div>
                    <span className="text-[10px] text-[color:var(--ink-soft)] flex-shrink-0">
                      {r.created_at ? new Date(r.created_at).toLocaleDateString("ru-RU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                    </span>
                  </div>
                  <div className="text-xs text-[color:var(--ink-soft)] mt-0.5 flex items-center gap-2">
                    <span>От: {reporter.first_name || "—"}</span>
                    <span className="w-1 h-1 rounded-full bg-[color:var(--ink-soft)]" />
                    <span>{r.reason_label || r.reason}</span>
                  </div>
                  {r.comment && !isOpen && (
                    <div className="text-xs text-[color:var(--ink)] mt-1 truncate">
                      «{r.comment}»
                    </div>
                  )}
                </div>
                <div className="flex-shrink-0">
                  <ChevronDown className={`w-4 h-4 text-[color:var(--ink-soft)] transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </div>
              </button>

              {/* Expanded details */}
              {isOpen && (
                <div className="px-4 pb-4 pt-0 border-t border-[color:var(--border)] bg-[color:var(--peach-bg)]/30">
                  {r.comment && (
                    <div className="mt-3 text-sm text-[color:var(--ink)] bg-white rounded-2xl px-4 py-3 border border-[color:var(--border)]">
                      <div className="text-[10px] uppercase tracking-wider text-[color:var(--ink-soft)] mb-1 font-semibold">Комментарий</div>
                      «{r.comment}»
                    </div>
                  )}
                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-[color:var(--ink-soft)]">
                    <div><b>ID жалобы:</b> {r.id.slice(0, 8)}</div>
                    <div><b>ID репортёра:</b> {r.reporter_id.slice(0, 8)}</div>
                    <div><b>ID нарушителя:</b> {r.reported_user_id.slice(0, 8)}</div>
                    <div><b>Дата:</b> {r.created_at ? new Date(r.created_at).toLocaleString("ru-RU") : "—"}</div>
                  </div>
                  {r.status === "open" && (
                    <div className="flex items-center gap-2 mt-3">
                      <button
                        onClick={() => handleAction(r.id, "resolve")}
                        disabled={busyId === r.id}
                        className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-[color:var(--teal-superlike)] to-green-500 text-white text-xs font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50 hover:shadow-lg transition-all"
                      >
                        {busyId === r.id ? (
                          <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                        ) : (
                          <><Check className="w-4 h-4" /> Принять жалобу</>
                        )}
                      </button>
                      <button
                        onClick={() => handleAction(r.id, "dismiss")}
                        disabled={busyId === r.id}
                        className="flex-1 py-2.5 rounded-xl bg-white border border-[color:var(--border)] text-[color:var(--ink)] text-xs font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50 hover:bg-gray-50 transition-all"
                      >
                        <X className="w-4 h-4" /> Отклонить
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function UtmPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/stats/utm");
      setData(d);
    } catch {
      toast.error("Ошибка загрузки UTM-статистики");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="text-center py-10 text-[color:var(--ink-soft)]">Загружаем…</div>;
  if (!data) return null;

  return (
    <div className="space-y-4">
      <div className="rounded-3xl bg-white/75 backdrop-blur-sm border border-[color:var(--border)] p-4 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--ink-soft)]">UTM-аналитика</div>
          <div className="text-sm text-[color:var(--ink-soft)] mt-1">
            Отслеживание источников трафика
          </div>
        </div>
        <button onClick={load} className="btn-pill bg-[color:var(--ink)] text-white text-sm px-4">
          <RefreshCw className="w-4 h-4 mr-1" /> Обновить
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-3xl bg-white border border-[color:var(--border)] p-4">
          <div className="text-[11px] uppercase tracking-widest text-[color:var(--ink-soft)]">Всего с UTM</div>
          <div className="font-[Onest] font-extrabold text-3xl mt-1">{data.total_utm || 0}</div>
        </div>
        <div className="rounded-3xl bg-white border border-[color:var(--border)] p-4">
          <div className="text-[11px] uppercase tracking-widest text-[color:var(--ink-soft)]">Без UTM</div>
          <div className="font-[Onest] font-extrabold text-3xl mt-1">{data.without_utm || 0}</div>
        </div>
        <div className="rounded-3xl bg-white border border-[color:var(--border)] p-4">
          <div className="text-[11px] uppercase tracking-widest text-[color:var(--ink-soft)]">Источников</div>
          <div className="font-[Onest] font-extrabold text-3xl mt-1">{Object.keys(data.by_source || {}).length}</div>
        </div>
        <div className="rounded-3xl bg-white border border-[color:var(--border)] p-4">
          <div className="text-[11px] uppercase tracking-widest text-[color:var(--ink-soft)]">Регистраций</div>
          <div className="font-[Onest] font-extrabold text-3xl mt-1">{data.total_users || 0}</div>
        </div>
      </div>

      {/* By source */}
      {data.by_source && Object.keys(data.by_source).length > 0 && (
        <div className="rounded-3xl bg-white border border-[color:var(--border)] p-5">
          <div className="text-sm font-semibold text-[color:var(--ink-soft)] mb-3 flex items-center gap-2">
            <ExternalLink className="w-4 h-4" />
            По источникам (utm_source)
          </div>
          <div className="space-y-2">
            {Object.entries(data.by_source).sort((a, b) => b[1] - a[1]).map(([source, count]) => {
              const pct = data.total_utm ? Math.round((count / data.total_utm) * 100) : 0;
              return (
                <div key={source} className="flex items-center gap-3">
                  <div className="w-24 text-xs font-semibold truncate flex-shrink-0">{source || "(пусто)"}</div>
                  <div className="flex-1 h-3 rounded-full bg-[color:var(--peach)]/15 overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-[color:var(--peach)] to-[color:var(--peach-deep)]" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="w-16 text-xs text-right text-[color:var(--ink-soft)]">{count} ({pct}%)</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* By medium */}
      {data.by_medium && Object.keys(data.by_medium).length > 0 && (
        <div className="rounded-3xl bg-white border border-[color:var(--border)] p-5">
          <div className="text-sm font-semibold text-[color:var(--ink-soft)] mb-3 flex items-center gap-2">
            <Filter className="w-4 h-4" />
            По каналам (utm_medium)
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(data.by_medium).sort((a, b) => b[1] - a[1]).map(([medium, count]) => (
              <div key={medium} className="px-3 py-2 rounded-xl bg-white border border-[color:var(--border)] text-xs flex items-center gap-2">
                <span className="font-semibold">{medium || "(пусто)"}</span>
                <span className="text-[color:var(--ink-soft)]">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent UTM users table */}
      {data.recent && data.recent.length > 0 && (
        <div className="rounded-3xl bg-white border border-[color:var(--border)] overflow-hidden">
          <div className="p-4 border-b border-[color:var(--border)]">
            <div className="text-sm font-semibold text-[color:var(--ink-soft)] flex items-center gap-2">
              <Users className="w-4 h-4" />
              Последние регистрации с UTM
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-[color:var(--peach-bg)] text-[color:var(--ink-soft)]">
                  <th className="text-left p-3 font-semibold">Имя</th>
                  <th className="text-left p-3 font-semibold">TG ID</th>
                  <th className="text-left p-3 font-semibold">Source</th>
                  <th className="text-left p-3 font-semibold">Medium</th>
                  <th className="text-left p-3 font-semibold">Дата</th>
                </tr>
              </thead>
              <tbody>
                {data.recent.map((u) => (
                  <tr key={u.id} className="border-t border-[color:var(--border)] hover:bg-[color:var(--peach-bg)]/30">
                    <td className="p-3 font-medium">{u.first_name}</td>
                    <td className="p-3 text-[color:var(--ink-soft)]">{u.telegram_id || "—"}</td>
                    <td className="p-3"><span className="px-2 py-0.5 rounded-full bg-[color:var(--peach)]/10 text-[color:var(--peach-deep)] font-semibold">{u.utm_source || "—"}</span></td>
                    <td className="p-3">{u.utm_medium || "—"}</td>
                    <td className="p-3 text-[color:var(--ink-soft)]">{u.created_at ? new Date(u.created_at).toLocaleDateString("ru-RU") : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function ReferralsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/stats/referrals");
      setData(d);
    } catch {
      toast.error("Ошибка загрузки статистики по рефералам");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="text-center py-10 text-[color:var(--ink-soft)]">Загружаем…</div>;
  if (!data) return null;

  return (
    <div className="space-y-4">
      <div className="rounded-3xl bg-white/75 backdrop-blur-sm border border-[color:var(--border)] p-4 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--ink-soft)]">Реферальная программа</div>
          <div className="text-sm text-[color:var(--ink-soft)] mt-1">
            +{data.reward_days} день Premium за друга · кап {data.weekly_cap_days} дн/нед · анти-фрода нет — следим тут
          </div>
        </div>
        <button onClick={load} className="btn-pill bg-[color:var(--ink)] text-white text-sm px-4">
          <RefreshCw className="w-4 h-4 mr-1" /> Обновить
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="rounded-3xl bg-white border border-[color:var(--border)] p-4">
          <div className="text-[11px] uppercase tracking-widest text-[color:var(--ink-soft)]">Приглашено всего</div>
          <div className="font-[Onest] font-extrabold text-3xl mt-1">{data.total_referred || 0}</div>
        </div>
        <div className="rounded-3xl bg-white border border-[color:var(--border)] p-4">
          <div className="text-[11px] uppercase tracking-widest text-[color:var(--ink-soft)]">Начислений</div>
          <div className="font-[Onest] font-extrabold text-3xl mt-1">{data.total_rewards || 0}</div>
        </div>
        <div className="rounded-3xl bg-white border border-[color:var(--border)] p-4">
          <div className="text-[11px] uppercase tracking-widest text-[color:var(--ink-soft)]">Дней Premium выдано</div>
          <div className="font-[Onest] font-extrabold text-3xl mt-1">{data.total_days_granted || 0}</div>
        </div>
      </div>

      {/* Top referrers */}
      {data.top_referrers && data.top_referrers.length > 0 && (
        <div className="rounded-3xl bg-white border border-[color:var(--border)] overflow-hidden">
          <div className="p-4 border-b border-[color:var(--border)]">
            <div className="text-sm font-semibold text-[color:var(--ink-soft)] flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Топ рефереров
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-[color:var(--peach-bg)] text-[color:var(--ink-soft)]">
                  <th className="text-left p-3 font-semibold">Имя</th>
                  <th className="text-left p-3 font-semibold">TG ID</th>
                  <th className="text-left p-3 font-semibold">Приглашено</th>
                  <th className="text-left p-3 font-semibold">Дней выдано</th>
                  <th className="text-left p-3 font-semibold">Забанено из приглашённых</th>
                </tr>
              </thead>
              <tbody>
                {data.top_referrers.map((r) => (
                  <tr key={r.id} className="border-t border-[color:var(--border)] hover:bg-[color:var(--peach-bg)]/30">
                    <td className="p-3 font-medium">{r.first_name}</td>
                    <td className="p-3 text-[color:var(--ink-soft)]">{r.telegram_id || "—"}</td>
                    <td className="p-3">{r.referred_total}</td>
                    <td className="p-3"><span className="px-2 py-0.5 rounded-full bg-[color:var(--peach)]/10 text-[color:var(--peach-deep)] font-semibold">{r.days_granted}</span></td>
                    <td className="p-3">
                      {r.referred_banned > 0 ? (
                        <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold">{r.referred_banned}</span>
                      ) : "0"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function PremiumPricingPanel() {
  const [tiers, setTiers] = useState(null);
  const [prices, setPrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/premium/pricing");
      setTiers(data.tiers);
      const p = {};
      data.tiers.forEach((tr) => { p[tr.days] = tr.stars; });
      setPrices(p);
    } catch {
      toast.error("Ошибка загрузки цен");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.entries(prices).forEach(([days, stars]) => { payload[days] = parseInt(stars, 10); });
      await api.post("/admin/premium/pricing", payload);
      toast.success("Цены сохранены");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center py-10 text-[color:var(--ink-soft)]">Загружаем…</div>;
  if (!tiers) return null;

  return (
    <div className="space-y-4">
      <div className="rounded-3xl bg-white/75 backdrop-blur-sm border border-[color:var(--border)] p-4 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--ink-soft)]">Цены Premium</div>
          <div className="text-sm text-[color:var(--ink-soft)] mt-1">
            Стоимость тарифов в Telegram Stars ⭐
          </div>
        </div>
        <button onClick={load} className="btn-pill bg-[color:var(--ink)] text-white text-sm px-4">
          <RefreshCw className="w-4 h-4 mr-1" /> Обновить
        </button>
      </div>

      <div className="rounded-3xl bg-white border border-[color:var(--border)] p-5 grid grid-cols-2 md:grid-cols-4 gap-3">
        {tiers.map((tr) => (
          <div key={tr.days}>
            <div className="text-[11px] uppercase tracking-widest text-[color:var(--ink-soft)] mb-1">
              {PREMIUM_TIER_LABELS[tr.days] || `${tr.days} дней`}
            </div>
            <div className="relative">
              <input
                type="number"
                min="1"
                step="1"
                value={prices[tr.days] ?? ""}
                onChange={(e) => setPrices((curr) => ({ ...curr, [tr.days]: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-xl bg-white border border-[color:var(--border)] focus:outline-none focus:ring-2 focus:ring-[color:var(--peach)]/40 text-sm font-semibold"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[color:var(--stars-gold)]">⭐</span>
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={save}
        disabled={saving}
        className="btn-pill bg-[color:var(--ink)] text-white text-sm px-6 disabled:opacity-50"
      >
        {saving ? "Сохраняем…" : "Сохранить цены"}
      </button>
    </div>
  );
}

export default function Admin() {
  const { authed } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("reports");
  const [checking, setChecking] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [authMode, setAuthMode] = useState(null);
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [requestId, setRequestId] = useState("");
  const [code, setCode] = useState("");
  const [authBusy, setAuthBusy] = useState(false);

  const checkAccess = async () => {
    setChecking(true);
    try {
      const { data } = await api.get("/admin/me");
      setAuthorized(!!data.is_admin);
      setAuthMode(data.mode || null);
    } catch {
      setAuthorized(false);
      setAuthMode(null);
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    checkAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startAdminAuth = async () => {
    if (!login.trim() || !password.trim()) {
      toast.error("Введите логин и пароль");
      return;
    }
    setAuthBusy(true);
    try {
      const { data } = await api.post("/admin/auth/start", {
        login: login.trim(),
        password,
      });
      setRequestId(data.request_id);
      toast.success("Код отправлен главному админу в Telegram");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Не удалось запустить 2FA");
    } finally {
      setAuthBusy(false);
    }
  };

  const verifyAdminAuth = async () => {
    if (!requestId || !code.trim()) {
      toast.error("Введите код 2FA");
      return;
    }
    setAuthBusy(true);
    try {
      const { data } = await api.post("/admin/auth/verify", {
        request_id: requestId,
        code: code.trim(),
      });
      setAdminToken(data.admin_token);
      setCode("");
      await checkAccess();
      toast.success("Вход в админку выполнен");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Неверный код");
    } finally {
      setAuthBusy(false);
    }
  };

  const adminLogout = async () => {
    try {
      if (getAdminToken()) {
        await api.post("/admin/auth/logout");
      }
    } catch {}
    setAdminToken(null);
    setAuthorized(false);
    setAuthMode(null);
    setRequestId("");
    setCode("");
    toast.success("Сессия админки завершена");
  };

  if (checking) {
    return (
      <div className="kupidon-shell flex items-center justify-center">
        <div className="text-[color:var(--ink-soft)]">Проверяем доступ…</div>
      </div>
    );
  }
  if (!authorized) {
    return (
      <div className="kupidon-shell safe-top safe-bottom flex flex-col items-center justify-center px-6 text-center gap-4">
        <div className="w-full max-w-md rounded-3xl bg-white border border-[color:var(--border)] p-5 text-left shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <LockKeyhole className="w-5 h-5 text-[color:var(--peach-deep)]" />
            <div className="font-[Onest] font-extrabold text-xl">Вход в админку</div>
          </div>
          <div className="text-sm text-[color:var(--ink-soft)] mb-4">
            Введите логин/пароль, затем подтвердите вход 2FA-кодом из Telegram главного админа.
          </div>

          {!requestId ? (
            <div className="space-y-3">
              <input
                data-testid="admin-login"
                value={login}
                onChange={(e) => setLogin(e.target.value)}
                placeholder="Логин"
                className="w-full px-4 py-3 rounded-2xl bg-white border border-[color:var(--border)] focus:outline-none focus:ring-2 focus:ring-[color:var(--peach)]"
              />
              <input
                data-testid="admin-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Пароль"
                className="w-full px-4 py-3 rounded-2xl bg-white border border-[color:var(--border)] focus:outline-none focus:ring-2 focus:ring-[color:var(--peach)]"
              />
              <button
                data-testid="admin-start-auth"
                onClick={startAdminAuth}
                disabled={authBusy}
                className="btn-pill w-full bg-[color:var(--ink)] text-white disabled:opacity-60"
              >
                {authBusy ? "Проверяем..." : "Получить 2FA код"}
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-xs text-[color:var(--ink-soft)]">Код отправлен в Telegram главного админа</div>
              <div className="relative">
                <KeyRound className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[color:var(--ink-soft)]" />
                <input
                  data-testid="admin-2fa-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="6-значный код"
                  className="w-full pl-10 pr-4 py-3 rounded-2xl bg-white border border-[color:var(--border)] focus:outline-none focus:ring-2 focus:ring-[color:var(--peach)]"
                />
              </div>
              <button
                data-testid="admin-verify-auth"
                onClick={verifyAdminAuth}
                disabled={authBusy}
                className="btn-pill w-full bg-[color:var(--peach)] text-white disabled:opacity-60"
              >
                {authBusy ? "Подтверждаем..." : "Войти"}
              </button>
              <button
                data-testid="admin-reset-auth"
                onClick={() => { setRequestId(""); setCode(""); }}
                className="w-full text-sm text-[color:var(--ink-soft)] underline"
              >
                Ввести логин заново
              </button>
            </div>
          )}
        </div>

        <div className="text-xs text-[color:var(--ink-soft)] text-center max-w-md">
          {authed
            ? "Если вы вошли через Telegram как админ, доступ также откроется автоматически."
            : "Вход через сайт доступен даже без авторизации в Telegram WebApp."}
        </div>
        <button
          data-testid="admin-back-home"
          onClick={() => nav(authed ? "/discover" : "/")}
          className="btn-pill bg-[color:var(--peach)] text-white px-6"
        >
          Назад
        </button>
      </div>
    );
  }

  return (
    <div className="kupidon-shell admin-shell safe-top safe-bottom flex flex-col relative" data-testid="admin-page">
      <div className="sticky top-0 z-20 w-full border-b border-[color:var(--border)] bg-[color:var(--peach-bg)]/85 backdrop-blur-lg">
        <div className="w-full max-w-7xl mx-auto px-5 pt-4 pb-2 flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--ink-soft)]">Admin</div>
          <div className="font-[Onest] font-extrabold text-2xl -mt-0.5">Kupidon REBORN</div>
          <div className="text-xs text-[color:var(--ink-soft)] mt-0.5">
            Режим входа: {authMode === "panel" ? "логин + 2FA" : "Telegram"}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {authMode === "panel" && (
            <button
              data-testid="admin-logout-panel"
              onClick={adminLogout}
              className="px-3 py-2 rounded-xl bg-white border border-[color:var(--border)] text-sm"
            >
              Выйти
            </button>
          )}
          <button
            data-testid="admin-exit"
            onClick={() => nav(authed ? "/discover" : "/")}
            className="w-10 h-10 rounded-2xl bg-white/70 flex items-center justify-center"
          >
            <LogOut className="w-5 h-5 text-[color:var(--ink)]" />
          </button>
        </div>
      </div>

      <div className="w-full max-w-7xl mx-auto px-5 pb-3 mt-2 flex gap-2 overflow-x-auto no-scrollbar">
        {TABS.map((t2) => (
          <button
            key={t2.id}
            data-testid={`admin-tab-${t2.id}`}
            onClick={() => setTab(t2.id)}
            className={`flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${
              tab === t2.id
                ? "bg-[color:var(--ink)] text-white"
                : "bg-white/70 text-[color:var(--ink)] border border-[color:var(--border)]"
            }`}
          >
            <t2.icon className="w-4 h-4" />
            {t2.label}
          </button>
        ))}
      </div>
      </div>

      <div className="flex-1 overflow-y-auto no-scrollbar w-full max-w-7xl mx-auto p-5">
        <motion.div key={tab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          {tab === "stats" && <StatsPanel />}
          {tab === "users" && <UsersPanel />}
          {tab === "reports" && <ReportsPanel />}
          {tab === "utm" && <UtmPanel />}
          {tab === "referrals" && <ReferralsPanel />}
          {tab === "premium" && <PremiumPricingPanel />}
        </motion.div>
      </div>
    </div>
  );
}
